import dayjs from 'dayjs'
import { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { defaultLpsInfos } from '@/constants/dmp-connect'
import { PatientIdentityStatus } from '@/core/models/patient.model'
import { useCheckInsIdentityQuery } from '@/core/services/dmp-connect-js2'
import { useGetPatientQuery } from '@/core/services/patient'

import { selectDMPConnectTlsiConnector } from '@/redux/reducers/dmp-connect.reducer'
import { useAppSelector } from '@/redux/rootReducer'

import { patientGenderToInsSex } from '@/utils/patient'

export function useIdentityStatus(patientId?: string) {
  const { billingNumber, instanceUniqueId: tlsiConnectorId } = useAppSelector(selectDMPConnectTlsiConnector) ?? {}
  const { data: patient } = useGetPatientQuery(patientId ?? skipToken)

  const { insNIR, insOID, userInfo } = patient ?? {}
  const { birthPlaceCode, birthday, gender, name, birthName, surname, birthSurname } = userInfo ?? {}

  const checkInsIdentityParams = useMemo(() => {
    const s_birthName = birthSurname ?? surname
    const s_given = birthName ?? name

    if (!insNIR || !insOID || !s_birthName || !s_given || !tlsiConnectorId) return
    return {
      LpsInfos: { ...defaultLpsInfos, s_billingNumber: billingNumber },
      i_sex: patientGenderToInsSex(gender ?? 'I'),
      s_birthDate: dayjs(birthday).format('YYYY-MM-DD'),
      s_birthName: s_birthName.trim().toUpperCase(),
      s_birthPlace: birthPlaceCode,
      s_given: s_given.trim().toUpperCase(),
      s_ins: insNIR.slice(0, 13),
      s_key: insNIR.slice(13),
      s_oid: insOID,
    }
  }, [billingNumber, birthPlaceCode, birthday, gender, name, birthName, surname, birthSurname, insNIR, insOID, tlsiConnectorId])

  const { isInsIdentityValid, error } = useCheckInsIdentityQuery(checkInsIdentityParams ?? skipToken, {
    selectFromResult: ({ data, error }) => ({ isInsIdentityValid: data ? data.s_status === 'OK' : undefined, error }),
  })

  const identityStatus = useMemo(() => {
    if (!patient) return null

    if (!patient.identityConfirmed && !isInsIdentityValid) {
      return PatientIdentityStatus.PROVISIONAL
    }
    if (!patient.identityConfirmed && isInsIdentityValid) {
      return PatientIdentityStatus.RECOVERED
    }
    if (patient.identityConfirmed && !isInsIdentityValid) {
      return PatientIdentityStatus.VALIDATED
    }
    return PatientIdentityStatus.QUALIFIED
  }, [patient, isInsIdentityValid])

  return { identityStatus, error }
}
